import { useEffect, useState } from "react"
import Button from "@mui/material/Button"
import Checkbox from "@mui/material/Checkbox"
import CircularProgress from "@mui/material/CircularProgress"
import Dialog from "@mui/material/Dialog"
import DialogActions from "@mui/material/DialogActions"
import DialogContent from "@mui/material/DialogContent"
import DialogTitle from "@mui/material/DialogTitle"
import FormControlLabel from "@mui/material/FormControlLabel"
import FormGroup from "@mui/material/FormGroup"
import Grid from "@mui/material/Grid"
import TextField from "@mui/material/TextField"
import Typography from "@mui/material/Typography"
import {
  AdminUserSchedule,
  UserScheduledWorkout,
  WORKOUT_DAYS,
  WorkoutDay,
} from "../models/UserScheduledWorkout"
import {
  deleteUserScheduleForAdmin,
  getUserScheduleForAdmin,
  putUserScheduleForAdmin,
} from "../services/user"
import {
  formatRequestErrorMessage,
  isFetchError,
  showRequestToast,
} from "../services/utils"
import { ConfirmDialog } from "./ConfirmDialog"

interface ScheduleEditDialogProps {
  open: boolean
  userId: number
  workout: UserScheduledWorkout | null
  onClose: () => void
  onSaved: () => void
}

export function ScheduleEditDialog({
  open,
  userId,
  workout,
  onClose,
  onSaved,
}: ScheduleEditDialogProps) {
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const [schedule, setSchedule] = useState<AdminUserSchedule | null>(null)
  const [startDate, setStartDate] = useState("")
  const [nrOfWeeks, setNrOfWeeks] = useState<number | "">("")
  const [workoutDays, setWorkoutDays] = useState<WorkoutDay[]>([])
  const [confirmAction, setConfirmAction] = useState<"save" | "delete" | null>(
    null
  )

  useEffect(() => {
    if (!open || !workout) {
      setSchedule(null)
      setConfirmAction(null)
      return
    }

    let cancelled = false

    const loadSchedule = async () => {
      setLoading(true)
      try {
        const result = await getUserScheduleForAdmin(
          userId,
          workout.editScheduleId
        )
        if (cancelled) return

        setSchedule(result)
        setStartDate((result.settings.startDate ?? "").split("T")[0])
        setNrOfWeeks(result.settings.nrOfWeeks ?? "")
        setWorkoutDays(
          (result.settings.workoutDays ?? []).filter((day): day is WorkoutDay =>
            WORKOUT_DAYS.includes(day as WorkoutDay)
          )
        )
      } catch (error) {
        if (!isFetchError(error)) {
          showRequestToast(formatRequestErrorMessage(error), "error")
        }
        onClose()
      } finally {
        if (!cancelled) {
          setLoading(false)
        }
      }
    }

    loadSchedule()

    return () => {
      cancelled = true
    }
  }, [open, userId, workout, onClose])

  const toggleWorkoutDay = (day: WorkoutDay) => {
    setWorkoutDays((current) =>
      current.includes(day)
        ? current.filter((value) => value !== day)
        : [...current, day]
    )
  }

  const handleSave = () => {
    if (!workout || !startDate || workoutDays.length === 0) {
      showRequestToast("Start date and at least one workout day are required.", "error")
      return
    }

    setConfirmAction("save")
  }

  const executeSave = async () => {
    if (!workout || !startDate || workoutDays.length === 0) return

    setSaving(true)
    try {
      await putUserScheduleForAdmin(userId, workout.editScheduleId, {
        settings: {
          startDate,
          workoutDays,
          ...(schedule?.editScope === "routine" && nrOfWeeks !== ""
            ? { nrOfWeeks: Number(nrOfWeeks) }
            : {}),
        },
      })
      showRequestToast("Schedule updated successfully.", "success")
      setConfirmAction(null)
      onSaved()
      onClose()
    } catch (error) {
      if (!isFetchError(error)) {
        showRequestToast(formatRequestErrorMessage(error), "error")
      }
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = () => {
    if (!workout) return
    setConfirmAction("delete")
  }

  const executeDelete = async () => {
    if (!workout) return

    setDeleting(true)
    try {
      await deleteUserScheduleForAdmin(userId, workout.editScheduleId)
      showRequestToast("Schedule deleted successfully.", "success")
      setConfirmAction(null)
      onSaved()
      onClose()
    } catch (error) {
      if (!isFetchError(error)) {
        showRequestToast(formatRequestErrorMessage(error), "error")
      }
    } finally {
      setDeleting(false)
    }
  }

  const closeConfirm = () => {
    if (saving || deleting) return
    setConfirmAction(null)
  }

  const handleConfirm = () => {
    if (confirmAction === "save") {
      void executeSave()
      return
    }

    if (confirmAction === "delete") {
      void executeDelete()
    }
  }

  const confirmLoading = confirmAction === "save" ? saving : deleting
  const confirmTitle =
    confirmAction === "delete" ? "Delete schedule?" : "Save schedule changes?"
  const confirmMessage =
    confirmAction === "delete"
      ? schedule?.editScope === "phase"
        ? `Delete the entire phase "${workout?.editLabel}" and all scheduled routines in it? This cannot be undone.`
        : `Delete the routine schedule "${workout?.editLabel}" and all of its scheduled workouts? This cannot be undone.`
      : schedule?.editScope === "phase"
        ? `Update the phase "${workout?.editLabel}" schedule? This will affect all routines in this phase.`
        : `Update the routine schedule "${workout?.editLabel}"?`
  const confirmLabel = confirmAction === "delete" ? "Delete" : "Save"
  const confirmColor = confirmAction === "delete" ? "error" : "primary"

  const busy = loading || saving || deleting

  return (
    <>
      <Dialog open={open} onClose={busy ? undefined : onClose} fullWidth maxWidth="sm">
      <DialogTitle>
        Edit {workout?.editScope === "phase" ? "Program Phase" : "Routine"}
      </DialogTitle>
      <DialogContent>
        {loading ? (
          <Grid container justifyContent="center" style={{ padding: 24 }}>
            <CircularProgress size={28} />
          </Grid>
        ) : (
          <>
            <Typography variant="subtitle1" gutterBottom>
              {workout?.editLabel}
            </Typography>
            {schedule?.editScope === "phase" && schedule.routinesInPhase?.length ? (
              <Typography variant="body2" color="textSecondary" gutterBottom>
                Affects routines in this phase:{" "}
                {schedule.routinesInPhase.map((routine) => routine.name).join(", ")}
              </Typography>
            ) : null}

            <Grid container spacing={2} style={{ marginTop: 8 }}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  type="date"
                  label="Start Date"
                  value={startDate}
                  onChange={(event) => setStartDate(event.target.value)}
                  InputLabelProps={{ shrink: true }}
                  inputProps={{
                    autoComplete: 'off',
                    'data-1p-ignore': true,
                    'data-lpignore': 'true',
                  }}
                  disabled={busy}
                />
              </Grid>
              {schedule?.editScope === "routine" ? (
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Number of Weeks"
                    value={nrOfWeeks}
                    onChange={(event) =>
                      setNrOfWeeks(
                        event.target.value === ""
                          ? ""
                          : Number(event.target.value)
                      )
                    }
                    InputLabelProps={{ shrink: true }}
                    disabled={busy}
                  />
                </Grid>
              ) : null}
              <Grid item xs={12}>
                <Typography variant="body2" gutterBottom>
                  Workout Days
                </Typography>
                <FormGroup row>
                  {WORKOUT_DAYS.map((day) => (
                    <FormControlLabel
                      key={day}
                      control={
                        <Checkbox
                          checked={workoutDays.includes(day)}
                          onChange={() => toggleWorkoutDay(day)}
                          disabled={busy}
                        />
                      }
                      label={day}
                    />
                  ))}
                </FormGroup>
              </Grid>
            </Grid>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>
          Cancel
        </Button>
        <Button color="error" onClick={handleDelete} disabled={busy || loading}>
          {deleting ? <CircularProgress size={20} /> : "Delete"}
        </Button>
        <Button
          color="primary"
          variant="contained"
          onClick={handleSave}
          disabled={busy || loading}
        >
          {saving ? <CircularProgress size={20} color="inherit" /> : "Save"}
        </Button>
      </DialogActions>
      </Dialog>
      <ConfirmDialog
        open={confirmAction !== null}
        title={confirmTitle}
        message={confirmMessage}
        confirmLabel={confirmLabel}
        confirmColor={confirmColor}
        loading={confirmLoading}
        onConfirm={handleConfirm}
        onCancel={closeConfirm}
      />
    </>
  )
}
